import {
  ROUTE_APPOINTMENTSFollwUps,
  ROUTE_ENQUIRY_DETAILS,
  ROUTE_INVOICE_HISTORY,
  ROUTE_MAIN_HOME,
  ROUTE_SUBSCRIPTION,
  ROUTE_TASKS,
  ROUTE_TEAM,
  ROUTE_UPDATE_CHECK_LIST,
  ROUTE_UPDATE_TASK,
  ROUTE_VISITOR_LOG,
} from "../VMConstants";

/** Dispatched when home should re-fetch today's updates (digest / reminders). */
export const FCM_HOME_REFRESH_EVENT = "vm:fcm-home-refresh";

export const DAILY_DIGEST_TYPE = "DAILY_DIGEST";

const APPOINTMENT_TYPES = new Set([
  "NEW_APPOINTMENT",
  "APPOINTMENT_UPDATED",
  "APPOINTMENT_DELETED",
  "APPOINTMENT_REMINDER",
]);

const FOLLOW_UP_TYPES = new Set([
  "NEW_FOLLOW_UP",
  "FOLLOW_UP_UPDATED",
  "FOLLOW_UP_DELETED",
  "FOLLOW_UP_REMINDER",
]);

const TASK_TYPES = new Set([
  "NEW_TASK",
  "TASK_UPDATED",
  "TASK_STATUS_UPDATED",
  "TASK_DELETED",
  "TASK_DUE_REMINDER",
]);

const CHECKLIST_TYPES = new Set([
  "NEW_CHECKLIST",
  "CHECKLIST_UPDATED",
  "CHECKLIST_STATUS_UPDATED",
  "CHECKLIST_DELETED",
]);

const ENQUIRY_TYPES = new Set([
  "NEW_ENQUIRY",
  "ENQUIRY_UPDATED",
  "ENQUIRY_STATUS_UPDATED",
  "ENQUIRY_EVENT_UPDATED",
  "ENQUIRY_DELETED",
  "ROOM_ALLOCATED",
]);

const INVOICE_TYPES = new Set([
  "INVOICE_CREATED",
  "INVOICE_PAYMENT",
]);

const VISITOR_TYPES = new Set(["VISITOR_CHECKIN", "VISITOR_CHECKOUT"]);

/** Types that should refresh the home "today" dashboard. */
const HOME_REFRESH_TYPES = new Set([
  DAILY_DIGEST_TYPE,
  "APPOINTMENT_REMINDER",
  "FOLLOW_UP_REMINDER",
  "TASK_DUE_REMINDER",
]);

/** Normalize FCM data map (all values are strings). */
export const normalizeNotificationData = (data = {}) => {
  const out = {};
  Object.keys(data || {}).forEach((key) => {
    const value = data[key];
    out[key] = value == null ? "" : String(value);
  });
  return out;
};

const countOrZero = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Fallback body for DAILY_DIGEST when notification.body is missing.
 * Matches backend copy: counts + clear-schedule message.
 */
export const formatDailyDigestBody = (rawData = {}) => {
  const data = normalizeNotificationData(rawData);
  const events = countOrZero(data.events);
  const enquiries = countOrZero(data.enquiries);
  const tasks = countOrZero(data.tasks);
  const checklists = countOrZero(data.checklists);
  const appointments = countOrZero(data.appointments);
  const followUps = countOrZero(data.follow_ups);

  const total =
    events + enquiries + tasks + checklists + appointments + followUps;

  if (total === 0) {
    return "Today you have a clear schedule. Have a great day!";
  }

  return `Today you have ${events} events, ${enquiries} enquiries, ${tasks} tasks, ${checklists} checklists, ${appointments} appointments, ${followUps} follow-ups.`;
};

export const shouldRefreshHomeForNotification = (rawData = {}) => {
  const data = normalizeNotificationData(rawData);
  return HOME_REFRESH_TYPES.has(data.type || "");
};

export const dispatchHomeRefreshForNotification = (rawData = {}) => {
  if (typeof window === "undefined") return;
  if (!shouldRefreshHomeForNotification(rawData)) return;
  window.dispatchEvent(
    new CustomEvent(FCM_HOME_REFRESH_EVENT, {
      detail: normalizeNotificationData(rawData),
    })
  );
};

/**
 * Map FCM `data.type` to an in-app route for this CRA router.
 * Prefer `data.type` over title/body for navigation.
 */
export const getNotificationRoute = (rawData = {}) => {
  const data = normalizeNotificationData(rawData);
  const type = data.type || "";
  const enquiryId = data.enquiry_id || data.entity_id || "";
  const entityId = data.entity_id || "";

  if (type === DAILY_DIGEST_TYPE) {
    return { pathname: `/${ROUTE_MAIN_HOME}`, state: null, search: "" };
  }

  if (APPOINTMENT_TYPES.has(type) || FOLLOW_UP_TYPES.has(type)) {
    if (enquiryId) {
      return {
        pathname: `/${ROUTE_ENQUIRY_DETAILS}`,
        state: { enquiry_id: enquiryId },
        search: `?enquiry_id=${encodeURIComponent(enquiryId)}`,
      };
    }
    return {
      pathname: `/${ROUTE_APPOINTMENTSFollwUps}`,
      state: null,
      search: "",
    };
  }

  if (TASK_TYPES.has(type)) {
    if (entityId) {
      return {
        pathname: `/${ROUTE_UPDATE_TASK}`,
        state: { taskId: entityId },
        search: `?taskId=${encodeURIComponent(entityId)}`,
      };
    }
    return { pathname: `/${ROUTE_TASKS}`, state: null, search: "" };
  }

  if (CHECKLIST_TYPES.has(type)) {
    if (entityId) {
      return {
        pathname: `/${ROUTE_UPDATE_CHECK_LIST}`,
        state: { checklistId: entityId },
        search: `?checklistId=${encodeURIComponent(entityId)}`,
      };
    }
    return { pathname: `/${ROUTE_TASKS}`, state: null, search: "" };
  }

  if (ENQUIRY_TYPES.has(type)) {
    const id = data.enquiry_id || data.entity_id || "";
    if (id) {
      return {
        pathname: `/${ROUTE_ENQUIRY_DETAILS}`,
        state: { enquiry_id: id },
        search: `?enquiry_id=${encodeURIComponent(id)}`,
      };
    }
    return { pathname: `/${ROUTE_MAIN_HOME}`, state: null, search: "" };
  }

  if (INVOICE_TYPES.has(type)) {
    const id = data.enquiry_id || data.entity_id || "";
    if (id) {
      return {
        pathname: `/${ROUTE_ENQUIRY_DETAILS}`,
        state: { enquiry_id: id },
        search: `?enquiry_id=${encodeURIComponent(id)}`,
      };
    }
    return { pathname: `/${ROUTE_INVOICE_HISTORY}`, state: null, search: "" };
  }

  if (type === "PAYMENT_SUCCESS") {
    return { pathname: `/${ROUTE_SUBSCRIPTION}`, state: null, search: "" };
  }

  if (VISITOR_TYPES.has(type)) {
    return { pathname: `/${ROUTE_VISITOR_LOG}`, state: null, search: "" };
  }

  if (type === "NEW_TEAM_MEMBER" || type === "UPDATE_TEAM_MEMBER") {
    return { pathname: `/${ROUTE_TEAM}`, state: null, search: "" };
  }

  return { pathname: `/${ROUTE_MAIN_HOME}`, state: null, search: "" };
};

/** Absolute path (+ query) for service worker `clients.openWindow`. */
export const getNotificationHref = (rawData = {}) => {
  const { pathname, search } = getNotificationRoute(rawData);
  return `${pathname}${search || ""}`;
};

export const navigateForNotification = (navigate, rawData = {}) => {
  if (typeof navigate !== "function") return;
  const data = normalizeNotificationData(rawData);
  const route = getNotificationRoute(data);
  if (route.state?.enquiry_id) {
    sessionStorage.setItem("enquiry_id", route.state.enquiry_id);
  }
  navigate(
    {
      pathname: route.pathname,
      search: route.search || "",
    },
    { state: route.state || undefined }
  );
  dispatchHomeRefreshForNotification(data);
};
